import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useApi } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  FileText,
  RefreshCw,
  Calendar,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Building2,
  ArrowRight,
  Sparkles,
  BarChart3,
  MessageSquare,
  Reply,
  Send
} from 'lucide-react';

export const ReportsPage = () => {
  const api = useApi();
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      const res = await api.get('/reports');
      setReports(res.data);
      if (res.data.length > 0 && !selectedReport) {
        setSelectedReport(res.data[0]);
      }
    } catch {
      // No reports yet
    } finally {
      setLoading(false);
    }
  }, [api, selectedReport]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleGenerate = async (offset = -1) => {
    setGenerating(true);
    try {
      const res = await api.post(`/reports/generate?week_offset=${offset}`);
      toast.success('Rapport généré !');
      setSelectedReport(res.data);
      fetchReports();
    } catch (err) {
      toast.error('Erreur lors de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const formatWeek = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    const opts = { day: 'numeric', month: 'short' };
    return `${s.toLocaleDateString('fr-FR', opts)} — ${e.toLocaleDateString('fr-FR', opts)} ${e.getFullYear()}`;
  };

  if (loading) {
    return (
      <MainLayout>
        <Header title="Rapports hebdomadaires" />
        <div className="p-6 flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-slate-400">Chargement...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Header title="Rapports hebdomadaires" subtitle="Suivi qualité et performance" />

      <div className="p-6 space-y-6 animate-fadeIn" data-testid="reports-page">
        {/* Generate actions */}
        <div className="flex items-center gap-3">
          <Button
            onClick={() => handleGenerate(-1)}
            disabled={generating}
            className="bg-[#FF5A6E] hover:bg-[#FF3B55] text-white"
            data-testid="generate-last-week-btn"
          >
            {generating ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
            Générer rapport semaine dernière
          </Button>
          <Button
            variant="outline"
            onClick={() => handleGenerate(0)}
            disabled={generating}
            data-testid="generate-this-week-btn"
          >
            <Calendar size={16} className="mr-2" />
            Semaine en cours
          </Button>
        </div>

        {reports.length === 0 && !selectedReport ? (
          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardContent className="py-16 text-center">
              <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="font-semibold text-slate-900 mb-2">Aucun rapport</h3>
              <p className="text-sm text-slate-500 mb-4">Générez votre premier rapport hebdomadaire pour commencer le suivi</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Reports list (left sidebar) */}
            <div className="lg:col-span-1 space-y-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Historique</p>
              {reports.map(r => (
                <button
                  key={r.id}
                  onClick={() => setSelectedReport(r)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedReport?.id === r.id
                      ? 'border-[#FF5A6E] bg-[#FFF0F1]'
                      : 'border-slate-100 bg-white hover:border-slate-200'
                  }`}
                  data-testid={`report-item-${r.id}`}
                >
                  <p className="text-sm font-medium text-slate-900">{formatWeek(r.week_start, r.week_end)}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    <span>{r.new_issues_count} problèmes</span>
                    <span className="text-emerald-500">{r.resolution_rate}% résolu</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Report detail (right) */}
            {selectedReport && (
              <div className="lg:col-span-3 space-y-6">
                {/* Week header */}
                <Card className="bg-gradient-to-r from-[#FF5A6E] to-[#FF3B55] text-white border-0">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar size={20} />
                      <h2 className="text-xl font-bold">{formatWeek(selectedReport.week_start, selectedReport.week_end)}</h2>
                    </div>
                    {selectedReport.ai_summary && (
                      <p className="text-white/90 text-sm leading-relaxed mt-3">{selectedReport.ai_summary}</p>
                    )}
                    <p className="text-white/60 text-xs mt-3">
                      Généré le {new Date(selectedReport.generated_at).toLocaleString('fr-FR')}
                    </p>
                  </CardContent>
                </Card>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <CardContent className="p-4 text-center">
                      <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-red-400" />
                      <p className="text-2xl font-bold text-slate-900">{selectedReport.new_issues_count}</p>
                      <p className="text-xs text-slate-500">Nouveaux problèmes</p>
                      <div className="flex justify-center gap-2 mt-1">
                        {selectedReport.major_issues_count > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded">{selectedReport.major_issues_count} majeurs</span>}
                        {selectedReport.minor_issues_count > 0 && <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded">{selectedReport.minor_issues_count} mineurs</span>}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <CardContent className="p-4 text-center">
                      <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-emerald-400" />
                      <p className="text-2xl font-bold text-slate-900">{selectedReport.resolved_issues_count}</p>
                      <p className="text-xs text-slate-500">Résolus</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <CardContent className="p-4 text-center">
                      <TrendingUp className="h-6 w-6 mx-auto mb-2 text-blue-400" />
                      <p className={`text-2xl font-bold ${selectedReport.resolution_rate >= 70 ? 'text-emerald-600' : selectedReport.resolution_rate >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                        {selectedReport.resolution_rate}%
                      </p>
                      <p className="text-xs text-slate-500">Taux résolution</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <CardContent className="p-4 text-center">
                      <Send className="h-6 w-6 mx-auto mb-2 text-violet-400" />
                      <p className="text-2xl font-bold text-slate-900">{selectedReport.published_count}</p>
                      <p className="text-xs text-slate-500">Publiés</p>
                      <div className="flex justify-center gap-2 mt-1">
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-50 text-slate-500 rounded">{selectedReport.reviews_generated} avis</span>
                        <span className="text-[10px] px-1.5 py-0.5 bg-slate-50 text-slate-500 rounded">{selectedReport.replies_generated} rép.</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Categories */}
                  <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <BarChart3 size={16} className="text-[#FF5A6E]" />
                        Problèmes par catégorie
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedReport.issues_by_category?.length > 0 ? (
                        <div className="space-y-3">
                          {selectedReport.issues_by_category.map(cat => {
                            const max = selectedReport.issues_by_category[0]?.count || 1;
                            return (
                              <div key={cat.category} className="flex items-center gap-3">
                                <span className="text-sm text-slate-700 w-24 truncate">{cat.category}</span>
                                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-[#FF5A6E] rounded-full" style={{ width: `${(cat.count / max) * 100}%` }} />
                                </div>
                                <span className="text-sm font-medium text-slate-900 w-6 text-right">{cat.count}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 text-center py-4">Aucune catégorie cette semaine</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Top properties */}
                  <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 size={16} className="text-[#FF5A6E]" />
                        Propriétés les plus touchées
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedReport.top_properties_with_issues?.length > 0 ? (
                        <div className="space-y-3">
                          {selectedReport.top_properties_with_issues.map((prop, i) => (
                            <div key={i} className="flex items-center gap-3 p-2 rounded-lg border border-slate-100">
                              <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-500">
                                {i + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{prop.property_name}</p>
                                <div className="flex gap-2 mt-0.5">
                                  {prop.major > 0 && <span className="text-[10px] text-red-600">{prop.major} majeur{prop.major > 1 ? 's' : ''}</span>}
                                  {prop.minor > 0 && <span className="text-[10px] text-amber-600">{prop.minor} mineur{prop.minor > 1 ? 's' : ''}</span>}
                                </div>
                              </div>
                              <span className="text-lg font-bold text-slate-900">{prop.issue_count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 text-center py-4">Aucune propriété touchée</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Critical comments */}
                {selectedReport.critical_comments?.length > 0 && (
                  <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle size={16} className="text-red-500" />
                        Commentaires critiques
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedReport.critical_comments.map(c => (
                          <Link
                            key={c.id}
                            to={`/issues/${c.id}`}
                            className="flex items-start gap-3 p-3 rounded-lg border border-red-100 bg-red-50/30 hover:bg-red-50 transition-colors"
                          >
                            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                              <AlertTriangle className="h-4 w-4 text-red-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900">{c.issue_summary || 'Problème majeur'}</p>
                              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                <span>{c.property_name}</span>
                                <span>{c.guest_name}</span>
                                {c.issue_categories?.map(cat => (
                                  <span key={cat} className="px-1.5 py-0.5 bg-white rounded text-[10px]">{cat}</span>
                                ))}
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              c.tracking_status === 'resolu' ? 'bg-emerald-100 text-emerald-700' :
                              c.tracking_status === 'en_cours' ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {c.tracking_status === 'resolu' ? 'Résolu' : c.tracking_status === 'en_cours' ? 'En cours' : 'Nouveau'}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Email placeholder */}
                <Card className="bg-slate-50 border-slate-200 border-dashed">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-600">Envoi par email</p>
                      <p className="text-xs text-slate-400">Fonctionnalité à venir — votre développeur pourra configurer le service d'envoi</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ReportsPage;
